import {
  Area,
  Booking,
  Bundle,
  Customer,
  GreeterEvent,
  LockedTable,
  Product,
  SpecialOpeningHours,
  TableId,
  TableServiceOrder,
  TableServiceOrderStatus,
  TableServiceSettings,
  Venue,
} from "@greeter/core";
import { DatePeriod } from "@greeter/date";
import { IVenueApi, UpdateTableServiceSettingsArgs } from "@greeter/venue-api";
import { compareAsc } from "date-fns";
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import {
  lockedTablesKeys,
  bookedTablesKeys,
  bookingKeys,
  floorPlanKeys,
  greeterEventKeys,
  productKeys,
  venueKeys,
  tableServiceOrderKeys,
  tableServiceSettingKeys,
  customerKeys,
  bundleKeys,
} from "./QueryKeys";
import { sortBy } from "lodash";
import { useMemo } from "react";
import { Query } from "@greeter/query";
import { useGreeterVenueApi } from "@greeter/matter";

export function useProductQuery(id?: string) {
  const { api } = useGreeterVenueApi();
  return useQuery({
    queryKey: productKeys.detail(id ?? ""),
    queryFn: async () => {
      if (id) return await api.fetchProduct(id);
    },
    enabled: !!id,
  });
}

export function useProductsQuery(options?: ReactQueryOptions<Product[]>) {
  const { api } = useGreeterVenueApi();
  const productsQuery = useQuery({
    queryKey: productKeys.all(),
    queryFn: async () => await api.fetchProducts({ page: 0, pageSize: 10000 }),
  });

  return productsQuery;
}

export function useBundlesQuery(options?: ReactQueryOptions<Bundle[]>) {
  const { api } = useGreeterVenueApi();
  return useQuery({
    queryKey: bundleKeys.all(),
    queryFn: async () => await api.fetchBundles({ page: 0, pageSize: 10000 }),
    ...options,
  });
}

export function useCustomerQuery(
  customerId?: string,
  options?: ReactQueryOptions<Customer | undefined>
) {
  const { api } = useGreeterVenueApi();
  return useQuery({
    queryKey: customerKeys.detail(customerId),
    queryFn: async () => {
      if (customerId)
        return (await api.fetchCustomers([customerId])).find(
          (c) => c.id === customerId
        );
    },
    enabled: !!customerId,
    ...options,
  });
}

export function useBookingQuery(
  bookingId?: string,
  options?: ReactQueryOptions<Booking | undefined>
) {
  const { api } = useGreeterVenueApi();
  return useQuery({
    queryKey: bookingKeys.detail(bookingId ?? ""),
    queryFn: () => {
      if (bookingId) return api.fetchBooking(bookingId);
    },
    enabled: !!bookingId,
    ...options,
  });
}

export function useVenueQuery(options?: Partial<ReactQueryOptions<Venue>>) {
  const { api } = useGreeterVenueApi();
  return useQuery({
    queryKey: venueKeys.self(),
    queryFn: api.fetchVenue.bind(api),
    ...options,
  });
}

export function useVenuesQuery(options?: { enabled?: boolean }) {
  const { api } = useGreeterVenueApi();
  return useQuery({
    queryKey: [],
    queryFn: async () =>
      sortBy(
        await api.fetchVenues(),
        (v) => v.id,
        (v) => v.address.city
      ),
    refetchOnWindowFocus: true,
    ...options,
  });
}

export type UseBookingsArgs = {
  period: DatePeriod;
  venueId?: string;
};
export function useBookingsQuery({ period }: UseBookingsArgs) {
  const queryClient = useQueryClient();
  const { api } = useGreeterVenueApi();
  const bookingsQuery = useQuery({
    queryKey: bookingKeys.all(period),
    queryFn: async () => {
      const bookings = await api.fetchBookings({
        period: period,
      });

      for (let i = 0; i < bookings.length; i++) {
        queryClient.setQueryData(["booking", bookings[i]!.id], bookings[i]!);
      }

      return bookings.sort((a, b) => compareAsc(a.period.from, b.period.from));
    },
  });

  return bookingsQuery;
}

export function useGreeterEventQuery(id?: string) {
  const { api } = useGreeterVenueApi();
  return useQuery({
    queryKey: greeterEventKeys.detail(id ?? ""),
    queryFn: async () => {
      if (id && id.trim() !== "") return await api.fetchGreeterEvent(id);
    },
  });
}

type ReactQueryOptions<T> = UseQueryOptions<T, Error, T, any[]>;

export function useBookedTablesQuery(
  date: Date,
  options?: Partial<ReactQueryOptions<TableId[]>>
) {
  const { api } = useGreeterVenueApi();
  return useQuery({
    queryKey: bookedTablesKeys.forDate(date),
    queryFn: async () => {
      if (date && api) {
        return await api.fetchBookedTables(date);
      }

      return [];
    },
    refetchInterval: 2000,
    ...options,
  });
}

export function useBookedTablesByPeriodQuery(
  period: DatePeriod,
  options?: Partial<ReactQueryOptions<TableId[]>>
) {
  const { api } = useGreeterVenueApi();
  return useQuery({
    queryKey: bookedTablesKeys.forDatePeriod(period),
    queryFn: async () => {
      if (period && api) {
        return await api.fetchBookedTablesByPeriod(period);
      }

      return [];
    },
    refetchInterval: 2000,
    ...options,
  });
}

export function useLockedTablesQuery(
  date: Date,
  options?: Partial<ReactQueryOptions<LockedTable[]>>
) {
  const { api } = useGreeterVenueApi();
  return useQuery({
    queryKey: lockedTablesKeys.forDate(date),
    queryFn: async () => {
      if (date && api) {
        return await api.fetchLockedTables(date);
      }

      return [];
    },
    refetchInterval: 2000,
    ...options,
  });
}

export function useLockedTablesByPeriodQuery(
  period: DatePeriod,
  options?: Partial<ReactQueryOptions<LockedTable[]>>
) {
  const { api } = useGreeterVenueApi();
  return useQuery({
    queryKey: lockedTablesKeys.forDatePeriod(period),
    queryFn: async () => {
      if (period && api) {
        return await api.fetchLockedTablesByPeriod(period);
      }
      return [];
    },
    refetchInterval: 2000,
    ...options,
  });
}

export function useGreeterEventsByPeriodQuery(
  period: DatePeriod,
  options?: Partial<ReactQueryOptions<GreeterEvent[]>>
) {
  const { api } = useGreeterVenueApi();
  return useQuery({
    queryKey: greeterEventKeys.forPeriod(period),
    queryFn: async () => {
      return await api.fetchGreeterEvents({ period: period });
    },
    ...options,
  });
}

export function useUpcomingGreeterEventsQuery(now: Date) {
  return useGreeterEventsByPeriodQuery(
    new DatePeriod({ from: now, to: new Date(Number.MAX_VALUE) })
  );
}

export function useFloorPlanQuery(
  options?: Partial<ReactQueryOptions<Area[]>>
) {
  const { api } = useGreeterVenueApi();
  const venue = useVenueQuery();
  return useQuery({
    queryKey: venue.data
      ? floorPlanKeys.venue(venue.data.id)
      : floorPlanKeys.all(),
    queryFn: async () => {
      return await api.fetchFloorPlan();
    },
    ...options,
  });
}

export function useTableServiceOrdersQuery(
  period: DatePeriod,
  options?: Partial<ReactQueryOptions<TableServiceOrder[]>>
) {
  const { api } = useGreeterVenueApi();
  return useQuery({
    queryKey: tableServiceOrderKeys.period(period),
    queryFn: async () => {
      return await api.fetchTableServiceOrders(period);
    },
    ...options,
  });
}

const updateTableServiceOrdersCache = (
  queryClient: QueryClient,
  period?: DatePeriod
) => {
  return (newTableServiceOrder: TableServiceOrder) => {
    if (!period) return;

    queryClient.setQueryData(
      tableServiceOrderKeys.period(period),
      (prev: TableServiceOrder[] | undefined) => {
        return (
          prev?.map((prevTableServiceOrder) =>
            prevTableServiceOrder.id === newTableServiceOrder.id
              ? newTableServiceOrder
              : prevTableServiceOrder
          ) ?? []
        );
      }
    );
  };
};

const optimisticUpdateTableServiceOrderStatus =
  (
    queryClient: QueryClient,
    status: TableServiceOrderStatus,
    period?: DatePeriod
  ) =>
  (id: string) => {
    if (!period) return;

    const key = tableServiceOrderKeys.period(period);

    const tableServiceOrders: TableServiceOrder[] =
      queryClient.getQueryData(key) ?? [];
    const tableServiceOrder = tableServiceOrders.find((tso) => tso.id === id);

    if (!tableServiceOrder) return;

    tableServiceOrder.status = status;

    // TODO: Copying entire array... Is it necessary?
    queryClient.setQueryData(key, [...tableServiceOrders]);
  };

type TableServiceOrderOptions = {
  period: DatePeriod;
};

export function useAcceptTableServiceOrdersMutation(
  options?: TableServiceOrderOptions
) {
  const queryClient = useQueryClient();
  const { api } = useGreeterVenueApi();
  return useMutation({
    mutationFn: api.acceptTableServiceOrder.bind(api),
    onMutate: optimisticUpdateTableServiceOrderStatus(queryClient, "accepted"),
    onSuccess: updateTableServiceOrdersCache(queryClient, options?.period),
  });
}

export function useCompleteTableServiceOrdersMutation(
  options?: TableServiceOrderOptions
) {
  const queryClient = useQueryClient();
  const { api } = useGreeterVenueApi();
  return useMutation({
    mutationFn: api.completeTableServiceOrder.bind(api),
    onMutate: optimisticUpdateTableServiceOrderStatus(queryClient, "completed"),
    onSuccess: updateTableServiceOrdersCache(queryClient, options?.period),
  });
}

export function useRejectTableServiceOrdersMutation(
  options?: TableServiceOrderOptions
) {
  const queryClient = useQueryClient();
  const { api } = useGreeterVenueApi();
  return useMutation({
    mutationFn: api.rejectTableServiceOrder.bind(api),
    onMutate: optimisticUpdateTableServiceOrderStatus(queryClient, "rejected"),
    onSuccess: updateTableServiceOrdersCache(queryClient, options?.period),
  });
}

export function useTableServiceSettingsQuery(
  options?: Partial<ReactQueryOptions<TableServiceSettings>>
) {
  const { api } = useGreeterVenueApi();
  return useQuery({
    queryKey: tableServiceSettingKeys.self(),
    queryFn: async () => {
      return await api.fetchTableServiceSettings();
    },
    ...options,
  });
}

export function useTableServiceSettingsMutation() {
  const queryClient = useQueryClient();
  const { api } = useGreeterVenueApi();
  return useMutation<
    TableServiceSettings,
    Error,
    UpdateTableServiceSettingsArgs
  >({
    mutationFn: api.updateTableServiceSettings.bind(api),
    onMutate: (data) => {
      queryClient.setQueryData(
        tableServiceSettingKeys.self(),
        (prev: TableServiceSettings | undefined) => ({ ...prev, ...data })
      );
    },
    onSuccess: (data) => {
      queryClient.setQueryData(tableServiceSettingKeys.self(), data);
    },
  });
}

export function useSpecialOpeningHoursQuery(
  venueId: string,
  options?: ReactQueryOptions<SpecialOpeningHours[]>
) {
  const { api } = useGreeterVenueApi();
  const q = useQuery({
    queryKey: ["venue", venueId, "specialOpeningHours"],
    queryFn: () => api.fetchSpecialOpeningHours(),
    ...options,
  });
  const s = useMemo(() => Query.mapQuery(q), [q]);

  return s;
}
